import React from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSelector } from "react-redux";
import {
  FaCheckCircle,
  FaShieldAlt,
  FaWhatsapp,
  FaPrint,
  FaCalendarAlt,
} from "react-icons/fa";

const BookingConfirmation = () => {
  const location = useLocation();
  const hotel = useSelector((state) => state.hotel.data) || {};
  const hotelName = hotel?.name || "Ganesh Holiday Homes";
  const hotelPhone = hotel?.phone || "+91 9673150591";
  const cleanPhone = hotelPhone.replace(/[^0-9]/g, "");

  // Passed via navigate state or query params (PayU redirect)
  const queryParams = new URLSearchParams(location.search);
  const stateData = location.state || {};

  const paymentStatus =
    queryParams.get("status") || stateData.status || "success";
  const txnid = queryParams.get("txnid") || stateData.txnid || "";
  const bookingId =
    queryParams.get("bookingId") ||
    queryParams.get("udf2") ||
    stateData.bookingId ||
    "GHH-BOOKING";
  const guestName =
    queryParams.get("name") ||
    queryParams.get("firstname") ||
    stateData.name ||
    "Valued Guest";
  const roomType =
    queryParams.get("roomType") || stateData.roomType || "Standard AC Room";
  const roomCount = Number(
    queryParams.get("roomCount") || stateData.roomCount || 1
  );
  const people = Number(queryParams.get("people") || stateData.people || 2);
  const child = Number(queryParams.get("child") || stateData.child || 0);
  const occupancyText = `${people} Adult${people > 1 ? "s" : ""}${child > 0 ? `, ${child} Child${child > 1 ? "ren" : ""}` : ""}`;
  const checkinDate =
    queryParams.get("checkinDate") ||
    stateData.checkinDate ||
    new Date().toISOString().split("T")[0];
  const checkoutDate =
    queryParams.get("checkoutDate") ||
    stateData.checkoutDate ||
    new Date(Date.now() + 86400000).toISOString().split("T")[0];
  const advancePaid = Number(
    queryParams.get("amount") || stateData.bookingAmount || 1000
  );
  const totalAmount = Number(
    queryParams.get("totalAmount") || stateData.totalAmount || advancePaid
  );
  const balanceDue = Math.max(0, totalAmount - advancePaid);
  const isFailed =
    paymentStatus.toLowerCase() === "failure" ||
    paymentStatus.toLowerCase() === "failed";

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      <Navbar />

      {/* Hero Banner */}
      <div
        style={{
          background: "linear-gradient(135deg, #07101e 0%, #0b192c 50%, #1e3e62 100%)",
          color: "#ffffff",
          padding: "45px 0 55px 0",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <div className="container text-center">
          <div className="d-inline-flex align-items-center gap-2 bg-success bg-opacity-25 text-white px-3 py-1 rounded-pill small fw-semibold mb-3 border border-success border-opacity-50">
            <FaCheckCircle className="text-success" />
            <span>Official Booking Acknowledgment Voucher</span>
          </div>
          <h1 className="h1 fw-bold mb-2 font-heading text-white" style={{ fontSize: "2.3rem" }}>
            Reservation Confirmation
          </h1>
          <p className="text-white-50 mb-0 mx-auto" style={{ maxWidth: "650px" }}>
            Booking Reference: <strong className="text-warning">{bookingId}</strong>
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="container flex-grow-1 py-5">
        <div
          className="mx-auto p-4 p-md-5 rounded-4 shadow-sm bg-white border"
          style={{ maxWidth: "850px" }}
        >
          {/* Header Card */}
          {isFailed ? (
            <div className="text-center mb-4 pb-3 border-bottom">
              <div
                className="bg-danger text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3 shadow"
                style={{ width: "64px", height: "64px", fontSize: "30px" }}
              >
                ✕
              </div>
              <h2 className="h4 fw-bold text-dark mb-1 font-heading">Payment Incomplete</h2>
              <p className="text-secondary small mb-2">
                We could not complete your online advance payment for booking <strong>{bookingId}</strong>.
              </p>
              <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 px-3 py-1 rounded-pill small fw-semibold">
                Payment Not Received • Inventory Unlocked
              </span>
            </div>
          ) : (
            <div className="text-center mb-4 pb-3 border-bottom">
              <div
                className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3 shadow"
                style={{ width: "64px", height: "64px", fontSize: "30px" }}
              >
                <FaCheckCircle />
              </div>
              <h2 className="h4 fw-bold text-dark mb-1 font-heading">Thank You, {guestName}!</h2>
              <p className="text-secondary small mb-2">
                Your room reservation request and token advance have been acknowledged.
              </p>
              <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-3 py-1 rounded-pill small fw-semibold">
                Advance Token Received • Inventory Locked
              </span>
            </div>
          )}

          {/* Verification notice */}
          <div className="p-3 bg-light rounded-4 border mb-4 d-flex align-items-start gap-3">
            <FaShieldAlt className={isFailed ? "text-danger fs-4 mt-1 flex-shrink-0" : "text-success fs-4 mt-1 flex-shrink-0"} />
            <div className="small text-secondary" style={{ lineHeight: "1.6" }}>
              {isFailed ? (
                <span>No charges were made to your account. You can retry your reservation or contact our front desk for assistance.</span>
              ) : (
                <span>Our front desk team is confirming the stay details and will transmit your formal stay voucher with directions via <strong>WhatsApp & Email</strong> shortly.</span>
              )}
            </div>
          </div>

          {/* Receipt Summary Table */}
          <div className="table-responsive mb-4 rounded-3 border">
            <table className="table table-bordered align-middle small mb-0">
              <thead className="table-dark">
                <tr>
                  <th colSpan="2" className="fw-bold">Stay & Room Details</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-secondary" style={{ width: "40%" }}>Property Name</td>
                  <td className="fw-bold text-dark">{hotelName} (Shrivardhan, Maharashtra)</td>
                </tr>
                <tr>
                  <td className="text-secondary">Guest Name</td>
                  <td className="fw-bold text-dark">{guestName}</td>
                </tr>
                <tr>
                  <td className="text-secondary">Room Category</td>
                  <td className="fw-bold text-dark">{roomType}</td>
                </tr>
                <tr>
                  <td className="text-secondary">Rooms & Occupancy</td>
                  <td>{roomCount} Room(s) • {occupancyText}</td>
                </tr>
                <tr>
                  <td className="text-secondary">Check-In Date</td>
                  <td><strong>{checkinDate}</strong> (Check-in from 12:00 PM)</td>
                </tr>
                <tr>
                  <td className="text-secondary">Check-Out Date</td>
                  <td><strong>{checkoutDate}</strong> (Check-out before 11:00 AM)</td>
                </tr>
                <tr className="table-dark">
                  <th colSpan="2" className="fw-bold">Payment & Balance Breakdown</th>
                </tr>
                {txnid && (
                  <tr>
                    <td className="text-secondary">PayU Transaction ID</td>
                    <td className="fw-bold text-dark font-monospace">{txnid}</td>
                  </tr>
                )}
                <tr>
                  <td className="text-secondary">Total Estimated Bill</td>
                  <td className="fw-bold text-dark">₹{Number(totalAmount).toLocaleString("en-IN")}</td>
                </tr>
                <tr>
                  <td className="text-secondary">Booking Advance Token Paid</td>
                  <td className="text-success fw-bold">₹{Number(advancePaid).toLocaleString("en-IN")}</td>
                </tr>
                <tr className="table-warning">
                  <td className="fw-bold text-dark">Balance Payable at Check-in</td>
                  <td className="fw-bold text-danger fs-6">₹{Number(balanceDue).toLocaleString("en-IN")}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Important Check-in Notes */}
          <div className="p-4 bg-light rounded-4 small text-secondary mb-4 border">
            <h6 className="fw-bold text-dark mb-2 font-heading">Important Check-In Guidelines:</h6>
            <ul className="mb-0 ps-3">
              <li className="mb-1">Please carry a valid Government-issued Photo ID (Aadhaar / Driving License / Passport) for all adult guests.</li>
              <li className="mb-1">Free gated car parking is available on premises for staying guests.</li>
              <li className="mb-0">Need to reschedule or cancel? View our <Link to="/cancellation-refund-policy" className="text-decoration-underline text-danger fw-semibold">Cancellation & Refund Policy</Link>.</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
            <button
              type="button"
              onClick={handlePrint}
              className="btn btn-outline-dark rounded-pill px-4 py-2 small fw-semibold d-inline-flex align-items-center justify-content-center gap-2"
            >
              <FaPrint /> Print Receipt
            </button>
            <a
              href={`https://wa.me/${cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone || "919673150591"}?text=${encodeURIComponent(
                `🏨 *Ganesh Holiday Homes - Booking Confirmation*\n\n` +
                `Hello Front Desk Team,\n` +
                `I have completed the payment for my reservation:\n\n` +
                `• *Booking Ref:* ${bookingId}\n` +
                `• *Guest Name:* ${guestName}\n` +
                `• *Check-in Date:* ${checkinDate}\n` +
                `• *Check-out Date:* ${checkoutDate}\n` +
                `• *Room:* ${roomType} (${roomCount} Room, ${occupancyText})\n` +
                `• *Advance Paid:* ₹${advancePaid}\n` +
                `• *Txn ID:* ${txnid || "Online"}\n\n` +
                `Please confirm my reservation and share the room voucher.\n` +
                `Thank you!`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-success rounded-pill px-4 py-2 small fw-semibold d-inline-flex align-items-center justify-content-center gap-2 shadow-sm"
            >
              <FaWhatsapp /> WhatsApp Front Desk
            </a>
            <Link
              to="/booking"
              className="btn btn-danger rounded-pill px-4 py-2 small fw-semibold d-inline-flex align-items-center justify-content-center gap-2 shadow-sm"
            >
              <FaCalendarAlt /> Book Another Room
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BookingConfirmation;
